import * as React from 'react';
import { Label } from '../Label';
import { Input } from '../Input';
import { Button } from '../Button';
import { Icons } from '../Icons';
import { cn } from '../../lib/utils';

/**
 * Scoped shadcn tokens so labels, inputs, and buttons stay readable on white
 * while the viewer/app root uses a dark palette (light --foreground).
 * Use on portaled surfaces too (e.g. Select dropdown).
 */
export const USER_PREFERENCES_LIGHT_SURFACE = cn(
  '[--background:0_0%_100%]',
  '[--foreground:240_10%_3.9%]',
  '[--card:0_0%_100%]',
  '[--card-foreground:240_10%_3.9%]',
  '[--popover:0_0%_100%]',
  '[--popover-foreground:240_10%_3.9%]',
  '[--muted:240_4.8%_95.9%]',
  '[--muted-foreground:240_3.8%_46.1%]',
  '[--border:240_5.9%_90%]',
  '[--input:240_5.9%_90%]',
  '[--accent:240_4.8%_95.9%]',
  '[--accent-foreground:240_5.9%_10%]',
  '[--secondary-foreground:240_5.9%_10%]',
  '[--primary:214_98%_60%]',
  '[--primary-foreground:0_0%_98%]',
  '[--ring:214_98%_60%]'
);

interface UserPreferencesModalProps {
  children: React.ReactNode;
  className?: string;
}

export function UserPreferencesModal({ children, className }: UserPreferencesModalProps) {
  return (
    <div
      className={cn(
        USER_PREFERENCES_LIGHT_SURFACE,
        'flex max-h-[85vh] w-full max-w-[720px] flex-col overflow-hidden rounded-none bg-white text-[#000000] sm:rounded-xl',
        className
      )}
    >
      {children}
    </div>
  );
}

interface HeaderBarProps {
  title: React.ReactNode;
  onClose: () => void;
  closeLabel?: string;
  className?: string;
}

function HeaderBar({ title, onClose, closeLabel = 'Close', className }: HeaderBarProps) {
  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-between gap-4 border-b border-[#EDEDED] bg-white px-6 py-3.5',
        className
      )}
    >
      <h2 className="text-[17px] font-bold leading-6 tracking-tight text-[#1A2B49]">{title}</h2>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-9 w-9 shrink-0 text-[#000000] hover:bg-[#F5F5F5] hover:text-[#000000]"
        onClick={onClose}
        aria-label={closeLabel}
        data-cy="close-button"
      >
        <Icons.Close className="h-5 w-5" />
      </Button>
    </div>
  );
}

/** Body
 *  Automatically wraps content in a scrollable area.
 */
interface BodyProps {
  children: React.ReactNode;
  className?: string;
  /** Classes for the inner padded column (spacing under the header, etc.). */
  contentClassName?: string;
}
function Body({ children, className, contentClassName }: BodyProps) {
  return (
    <div className={cn('flex-1 overflow-y-auto bg-white', className)}>
      <div className={cn('flex flex-col gap-6 px-6 py-4', contentClassName)}>{children}</div>
    </div>
  );
}

/** Subheading
 *  Section labels
 */
interface SubHeadingProps {
  children: React.ReactNode;
  className?: string;
}
function SubHeading({ children, className }: SubHeadingProps) {
  return <span className={cn('text-muted-foreground text-lg', className)}>{children}</span>;
}

/** Responsive 3-column grid for hotkeys, etc. */
interface HotkeysGridProps {
  children: React.ReactNode;
  className?: string;
}
function HotkeysGrid({ children, className }: HotkeysGridProps) {
  return (
    <div
      className={cn('grid grid-cols-1 gap-x-8 gap-y-2.5 sm:grid-cols-2', className)}
    >
      {children}
    </div>
  );
}

function formatHotkeyKeys(keys: string | string[] | undefined): string {
  if (keys === undefined || keys === null) {
    return '';
  }
  return Array.isArray(keys) ? keys.join('+') : keys;
}

/** A single hotkey row: label + input */
interface HotkeyProps {
  label: string;
  placeholder?: string;
  className?: string;
  value?: string | string[];
  onChange?: (value: string) => void;
  /** When true, shows the binding as static text (no recording or edits). */
  readOnly?: boolean;
  hotkeys?: {
    record: (callback: (sequence: string[]) => void) => void;
    pause: () => void;
    unpause: () => void;
    startRecording: () => void;
  };
}

function Hotkey({
  label,
  placeholder,
  className,
  value,
  onChange,
  hotkeys,
  readOnly,
}: HotkeyProps) {
  const keysText = formatHotkeyKeys(value);
  const [isRecording, setIsRecording] = React.useState(false);

  if (readOnly) {
    return (
      <div className={cn('flex items-center justify-between gap-3', className)}>
        <Label className="text-sm font-normal text-[#000000]">{label}</Label>
        <span className="inline-flex h-9 min-w-[4.5rem] cursor-default select-none items-center justify-center rounded-md border border-[#EDEDED] bg-[#F5F5F5] px-2 text-center text-sm font-medium text-[#000000]">
          {keysText}
        </span>
      </div>
    );
  }

  const onInputKeyDown = (event: React.KeyboardEvent) => {
    event.preventDefault();
    hotkeys?.record((sequence: string[]) => {
      const keys = sequence.join('+');
      hotkeys?.unpause();
      setIsRecording(false);
      onChange?.(keys);
    });
  };

  const onFocus = () => {
    setIsRecording(true);
    hotkeys?.pause();
    hotkeys?.startRecording();
  };

  const onBlur = () => {
    setIsRecording(false);
    hotkeys?.unpause();
  };

  return (
    <div className={cn('flex items-center justify-between gap-3', className)}>
      <Label className="text-sm font-normal text-[#000000]">{label}</Label>
      <Input
        className={cn(
          'h-9 w-[4.5rem] rounded-md border border-[#EDEDED] bg-[#F5F5F5] text-center text-sm text-[#000000] shadow-none transition-colors',
          isRecording && 'border-primary bg-primary/5 text-primary caret-primary'
        )}
        placeholder={isRecording ? 'Press keys...' : formatHotkeyKeys(placeholder)}
        value={keysText}
        onKeyDown={onInputKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        readOnly={!isRecording}
      />
    </div>
  );
}

/** Attach subcomponents as static properties for a nicer API */
UserPreferencesModal.Body = Body;
UserPreferencesModal.HeaderBar = HeaderBar;
UserPreferencesModal.HotkeysGrid = HotkeysGrid;
UserPreferencesModal.Hotkey = Hotkey;
UserPreferencesModal.SubHeading = SubHeading;
